/**
 * EnergiaAgora — quanto o cliente está puxando NESTE minuto, e a curva do
 * período.
 *
 * Duas decisões de forma que valem explicar, porque a primeira contraria o
 * pedido ao pé da letra ("um gauge grande"):
 *
 *  • NÃO é um ponteiro circular. O dado é "um valor contra um limite", e a
 *    forma certa pra isso é número grande + barra de razão. O mostrador
 *    redondo gasta muito espaço pra codificar um número só, e ângulo se lê
 *    pior que comprimento — em tela estreita ele vira enfeite. O que o
 *    pedido quer (ver de relance quanto está sendo consumido, contra o quê)
 *    está aqui inteiro: o número em 48px e a barra contra a potência
 *    instalada.
 *
 *  • A barra tem um LIMITE de verdade. Sem a nominal dos ativos, "2,4 kW"
 *    não diz se é muito ou pouco, e qualquer escala seria inventada. Quando
 *    o cadastro não tem nominal, a barra some em vez de mentir uma escala.
 *
 * Cor: âmbar #d97706 nos dois temas, validado contra as duas superfícies
 * (banda de luminosidade, piso de croma e contraste >= 3:1). Série única, uma
 * cor só — não há identidade pra distinguir, e por isso também não há legenda.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Zap } from "lucide-react";
import clsx from "clsx";

import type { TenantStats } from "@/types";

/** Uma cor, os dois temas: passou nas checagens em claro e em escuro. */
const AMBAR = "#d97706";

function formatW(w: number | null | undefined): string {
  if (w === null || w === undefined) return "—";
  if (Math.abs(w) >= 1000) return `${(w / 1000).toFixed(2).replace(".", ",")} kW`;
  return `${Math.round(w)} W`;
}

function formatKwh(v: number): string {
  // Abaixo de 10 kWh a segunda casa é o que distingue um dia do outro; acima
  // dela vira ruído.
  const casas = Math.abs(v) >= 10 ? 1 : 2;
  return `${v.toFixed(casas).replace(".", ",")} kWh`;
}

function formatHora(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

function formatDia(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "2-digit",
  });
}

export function EnergiaAgora({ data }: { data: TenantStats }) {
  const e = data.energy;
  // Acima de 24 h de período o servidor manda energia por dia, não potência
  // média. Ver `power_series_unit` em schemas.py pro porquê.
  const emEnergia = data.power_series_unit === "kWh";
  const serie = useMemo(
    () =>
      data.power_series
        .map((p) => (p.value !== null ? { t: new Date(p.time).getTime(), v: p.value, iso: p.time } : null))
        .filter((p): p is { t: number; v: number; iso: string } => p !== null),
    [data.power_series],
  );

  const temInstantaneo = e.instant_w !== null && e.medidores_frescos > 0;
  const frotaParcial =
    e.medidores_total > 0 && e.medidores_frescos < e.medidores_total;

  // Sem leitura fresca, o herói TROCA de grandeza em vez de virar um traço.
  //
  // "—" no número principal não informa nada e ainda dá a impressão de tela
  // quebrada. E o silêncio tem duas causas que se parecem: o compressor
  // desligado (medidor reportando 0 W — aí o instantâneo EXISTE e vale zero)
  // e o medidor que parou de falar. O primeiro caso nunca chega aqui; este
  // ramo é sempre o segundo, e por isso o rótulo muda junto: passa a dizer
  // "no período", que é uma afirmação verdadeira sobre um dado que existe.
  const heroi = temInstantaneo
    ? { rotulo: "Consumo agora", valor: formatW(e.instant_w), ajuda: null }
    : {
        rotulo: "Consumo no período",
        valor: `${e.total_kwh.toFixed(2).replace(".", ",")} kWh`,
        // A frase não pode contradizer o que está na tela.
        //
        // "nenhum medidor cadastrado" apareceu logo ACIMA de 10,46 kWh
        // medidos — por um medidor. Quando existe consumo no período, existe
        // medidor, ponto: qualquer contagem que diga o contrário está errada,
        // e a mensagem tem que acreditar no dado, não no contador.
        ajuda: e.samples > 0 || e.total_kwh > 0
          ? "sem leitura nos últimos 10 minutos — o instantâneo precisa de dado recente"
          : "nenhum medidor de energia reportando neste cliente",
      };

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-surface-200 p-4 dark:border-slate-700">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            <Zap className="h-3.5 w-3.5" style={{ color: AMBAR }} />
            {heroi.rotulo}
          </span>
          {/* Figura herói: números proporcionais, não tabulares — em corpo
              grande, `tabular-nums` deixa o número frouxo. */}
          <div className="mt-1 text-[44px] font-semibold leading-none text-slate-900 dark:text-slate-100">
            {heroi.valor}
          </div>
          <div className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
            {heroi.ajuda ?? (
              <>
                {e.medidores_frescos} de {e.medidores_total}{" "}
                {e.medidores_total === 1 ? "medidor" : "medidores"} reportando
                {frotaParcial && " — o total cobre só esses"}
              </>
            )}
          </div>
        </div>

        <div className="text-right text-xs text-slate-500 dark:text-slate-400">
          {temInstantaneo && (
            <div>
              no período{" "}
              <span className="font-semibold text-slate-700 dark:text-slate-300">
                {e.total_kwh.toFixed(2).replace(".", ",")} kWh
              </span>
            </div>
          )}
          <div className={temInstantaneo ? "mt-0.5" : ""}>
            média do período{" "}
            <span className="font-semibold text-slate-700 dark:text-slate-300">
              {formatW(e.avg_power_w)}
            </span>
          </div>
          {e.nominal_w ? (
            <div className="mt-0.5">
              instalado{" "}
              <span className="font-semibold text-slate-700 dark:text-slate-300">
                {formatW(e.nominal_w)}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      {/* Razão contra o limite instalado. Só existe quando há limite: escala
          inventada é pior que escala nenhuma. */}
      {temInstantaneo && e.nominal_w ? (
        <Medidor atual={e.instant_w!} limite={e.nominal_w} />
      ) : null}

      {/* O gráfico ganha título porque a grandeza dele muda com o período, e
          uma curva sem rótulo foi lida — corretamente, dado o que a tela
          dizia — como "o dia consumiu 80 W". Um número sem unidade nomeada é
          um convite a inventar a unidade. */}
      {serie.length >= 1 && (
        <div className="mt-4 text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {emEnergia ? "Consumo por dia" : "Potência ao longo do período"}
        </div>
      )}

      {/* A forma segue a grandeza, e a grandeza vem do servidor.

          Potência é contínua: existe em qualquer instante, e a linha convida
          a ler entre os pontos — que é exatamente o certo quando o bucket é
          de minutos. Energia por dia é um total FECHADO de um intervalo; ler
          entre duas barras não significa nada, e por isso ela é barra. */}
      {serie.length >= 1 && emEnergia ? (
        <BarrasDeEnergia pontos={serie} />
      ) : serie.length >= 2 ? (
        <SerieDePotencia pontos={serie} />
      ) : (
        <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
          Sem medições de potência suficientes no período para desenhar a curva.
        </p>
      )}
    </div>
  );
}

/** Barra de razão: preenchimento e trilho saem da MESMA rampa. */
function Medidor({ atual, limite }: { atual: number; limite: number }) {
  const pct = Math.max(0, Math.min(100, (atual / limite) * 100));
  return (
    <div className="mt-4">
      <div
        className="h-2.5 w-full overflow-hidden rounded-full bg-amber-100 dark:bg-amber-950/60"
        role="meter"
        aria-valuenow={Math.round(atual)}
        aria-valuemin={0}
        aria-valuemax={Math.round(limite)}
        aria-label="Consumo atual contra a potência instalada"
      >
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${pct}%`, background: AMBAR }}
        />
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-slate-500 dark:text-slate-400">
        <span>{pct.toFixed(0)}% da potência instalada</span>
        <span className="tabular-nums">{formatW(limite)}</span>
      </div>
    </div>
  );
}

/**
 * Curva do período. SVG só pros vetores; texto em HTML sobreposto — mesma
 * arquitetura do TempSparkline, pelo mesmo motivo: `preserveAspectRatio="none"`
 * estica os `<text>` de dentro e deforma a tipografia em tela larga.
 */
function SerieDePotencia({
  pontos,
}: {
  pontos: { t: number; v: number; iso: string }[];
}) {
  const [hover, setHover] = useState<number | null>(null);

  const w = 600;
  const h = 120;
  const padL = 8;
  const padR = 8;
  const padT = 10;
  const padB = 16;

  const vals = pontos.map((p) => p.v);
  const maxV = Math.max(...vals);
  // Base em zero: potência é magnitude, e cortar a base exagera variação.
  const yMax = maxV * 1.1 || 1;
  const t0 = pontos[0].t;
  const t1 = pontos[pontos.length - 1].t;
  const spanT = Math.max(1, t1 - t0);

  const x = (t: number) => padL + ((t - t0) / spanT) * (w - padL - padR);
  const y = (v: number) => h - padB - (v / yMax) * (h - padT - padB);

  const linha = pontos.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`).join(" ");
  const area = `${linha} L${x(t1).toFixed(1)},${h - padB} L${x(t0).toFixed(1)},${h - padB} Z`;

  const alvo = hover !== null ? pontos[hover] : null;

  const iPico = vals.indexOf(maxV);
  const pctPico = Math.min(92, Math.max(8, (x(pontos[iPico].t) / w) * 100));
  // O rótulo do pico some quando o tooltip chega perto: os dois ocupam a
  // mesma faixa no topo, e dois textos empilhados ali não se leem. Some o
  // fixo, porque o transitório é o que a pessoa pediu ao mover o cursor.
  const picoVisivel =
    !alvo || Math.abs((x(alvo.t) / w) * 100 - pctPico) > 18;

  return (
    <div className="relative mt-1 min-h-[90px] flex-1">
      <svg
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        className="h-full min-h-[90px] w-full"
        role="img"
        aria-label="Potência total do cliente ao longo do período"
        onMouseLeave={() => setHover(null)}
        onMouseMove={(ev) => {
          const caixa = ev.currentTarget.getBoundingClientRect();
          const rel = ((ev.clientX - caixa.left) / caixa.width) * w;
          const alvoT = t0 + ((rel - padL) / (w - padL - padR)) * spanT;
          let melhor = 0;
          let dist = Infinity;
          pontos.forEach((p, i) => {
            const d = Math.abs(p.t - alvoT);
            if (d < dist) { dist = d; melhor = i; }
          });
          setHover(melhor);
        }}
      >
        <defs>
          <linearGradient id="potencia-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={AMBAR} stopOpacity="0.28" />
            <stop offset="100%" stopColor={AMBAR} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <line
          x1={padL} y1={h - padB} x2={w - padR} y2={h - padB}
          className="text-slate-300 dark:text-slate-700"
          stroke="currentColor" strokeWidth="1"
        />
        <path d={area} fill="url(#potencia-fill)" />
        <path d={linha} fill="none" stroke={AMBAR} strokeWidth="2"
              strokeLinejoin="round" strokeLinecap="round"
              vectorEffect="non-scaling-stroke" />
        {alvo && (
          <line
            x1={x(alvo.t)} y1={padT} x2={x(alvo.t)} y2={h - padB}
            className="text-slate-400 dark:text-slate-500"
            stroke="currentColor" strokeWidth="1" strokeDasharray="3,3"
            vectorEffect="non-scaling-stroke"
          />
        )}
      </svg>

      {/* Pico rotulado direto na curva: um número em cada ponto seria ruído,
          e o pico é o que alguém procura numa curva de consumo.

          A posição é grampeada entre 8% e 92%: centrado no pico, o rótulo
          sai pela borda quando o pico cai no primeiro ou no último bucket —
          e o pico no começo do período é caso comum, não exceção. */}
      {picoVisivel && (
        <div
          className="pointer-events-none absolute text-[11px] font-semibold text-slate-600 dark:text-slate-300"
          style={{ left: `${pctPico}%`, top: 0, transform: "translateX(-50%)" }}
        >
          pico {formatW(maxV)}
        </div>
      )}

      {alvo && (
        <div
          className="pointer-events-none absolute -top-1 z-10 rounded-md border border-surface-200 bg-white px-2 py-1 text-[11px] shadow-sm dark:border-slate-700 dark:bg-slate-900"
          style={{
            left: `${(x(alvo.t) / w) * 100}%`,
            transform: `translateX(${x(alvo.t) > w / 2 ? "-105%" : "5%"})`,
          }}
        >
          <div className="font-semibold tabular-nums text-slate-800 dark:text-slate-100">
            {formatW(alvo.v)}
          </div>
          <div className="text-slate-500 dark:text-slate-400">{formatHora(alvo.iso)}</div>
        </div>
      )}

      <div className="mt-1 flex justify-between text-[10px] text-slate-400 dark:text-slate-500">
        <span>{formatHora(pontos[0].iso)}</span>
        <span>{formatHora(pontos[pontos.length - 1].iso)}</span>
      </div>
    </div>
  );
}

/**
 * Consumo por dia, em barras.
 *
 * Por que não SVG, como a curva: aqui a geometria tem medidas em PIXEL que
 * não podem escalar junto com o container — o canto de 4px no topo e o
 * respiro de 2px entre barras. Num `<svg preserveAspectRatio="none">` o `rx`
 * deforma em elipse quando o card é largo, e `vectorEffect` só salva traço,
 * não raio. Em HTML os dois são exatos em qualquer largura.
 *
 * Especificação das marcas: barra <= 24px de espessura (teto, não meta — a
 * sobra da faixa é ar), topo arredondado em 4px e base reta na linha zero,
 * 2px de superfície separando vizinhas. Série única, sem legenda: o título
 * acima já diz o que está plotado.
 */
function BarrasDeEnergia({
  pontos,
}: {
  pontos: { t: number; v: number; iso: string }[];
}) {
  const [hover, setHover] = useState<number | null>(null);

  // A ALTURA DAS BARRAS É MEDIDA, NÃO PERCENTUAL. Isto é um conserto, não
  // preciosismo.
  //
  // Antes cada barra era `height: N%`. Percentual só resolve contra um pai de
  // altura DEFINIDA — e no telefone não existe nenhuma na cadeia: abaixo de
  // `lg` a célula do gráfico deixa de ser absoluta, o card passa a ter altura
  // de conteúdo, e a faixa das barras é `flex-1`, cujo tamanho vem da
  // distribuição do flex e não conta como definido. O navegador resolve o
  // percentual contra `auto`, a barra vira zero, e o gráfico some — com a
  // caixa ocupando espaço normalmente, que é o que faz parecer dado faltando
  // em vez de bug de layout.
  //
  // No desktop funcionava por acidente: lá a célula é `absolute inset-0` e a
  // cadeia toda tem altura definida.
  //
  // A caixa SEMPRE tem altura na tela (o `min-h` garante) — o percentual é
  // que não conseguia enxergá-la. Medindo em pixel, a pergunta nem se coloca,
  // e o resultado é o mesmo em qualquer motor.
  const faixa = useRef<HTMLDivElement>(null);
  const [alturaPx, setAlturaPx] = useState(0);
  useEffect(() => {
    const el = faixa.current;
    if (!el) return;
    setAlturaPx(el.getBoundingClientRect().height);
    // Rotacionar o telefone, abrir o teclado ou recolher os indicadores muda
    // a faixa sem remontar o componente.
    const ro = new ResizeObserver(([e]) => setAlturaPx(e.contentRect.height));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Antes da primeira medida, desenha com o piso da caixa em vez de nada: um
  // quadro com barras quase certas se lê melhor que um quadro vazio.
  const alturaUtil = alturaPx || 90;

  const vals = pontos.map((p) => p.v);
  const maxV = Math.max(...vals);
  // Base em zero e teto com folga: energia é magnitude, e cortar a base
  // exagera a variação entre dias que são quase iguais.
  const yMax = maxV * 1.1 || 1;

  const iPico = vals.indexOf(maxV);
  const alvo = hover !== null ? pontos[hover] : null;
  // O rótulo do pico e o tooltip disputam a mesma faixa no topo. Some o fixo:
  // o transitório é o que a pessoa pediu ao mover o cursor.
  const picoVisivel = !alvo || Math.abs(hover! - iPico) > 2;

  // Com 90 barras o respiro de 2px come metade da faixa. Abaixo de 6px de
  // faixa ele cai pra 1px — separar continua sendo o objetivo, e uma barra de
  // 1px de largura não separa nada.
  const respiroFino = pontos.length > 60;

  // A espessura acompanha QUANTAS barras existem.
  //
  // Um teto fixo de 24px é a regra certa pra série densa e vira defeito na
  // série curta: com sete dias, sete fiapos de 24px num card largo deixam a
  // faixa quase toda vazia e o gráfico parece um erro de carregamento.
  //
  // O teto sobe, mas continua sendo TETO: mesmo em 48px a barra ocupa pouco
  // mais da metade da faixa, então a sobra continua sendo ar e o ritmo
  // horizontal continua legível. O que não pode é a barra encostar na
  // vizinha — aí o respiro deixaria de existir e o eixo vira um bloco só.
  const larguraMax =
    pontos.length <= 8 ? 48
    : pontos.length <= 16 ? 36
    : pontos.length <= 40 ? 28
    : 24;

  return (
    <div className="relative mt-1 flex min-h-[90px] flex-1 flex-col">
      <div
        ref={faixa}
        className={clsx("flex flex-1 items-end", respiroFino ? "gap-px" : "gap-0.5")}
        role="img"
        aria-label="Consumo de energia por dia no período"
        onMouseLeave={() => setHover(null)}
      >
        {pontos.map((p, i) => (
          // A faixa inteira é o alvo do mouse, não a barra: num dia de
          // consumo baixo a barra tem 3px de altura e seria impossível pegar.
          <div
            key={p.iso}
            className="flex flex-1 cursor-default flex-col justify-end"
            style={{ height: alturaUtil }}
            onMouseEnter={() => setHover(i)}
          >
            <div
              className="mx-auto w-full transition-[height] duration-300"
              style={{
                maxWidth: `${larguraMax}px`,
                // Pixel, não porcentagem — ver o comentário da medição acima.
                // O piso de 2px existe pra que um dia de consumo baixo apareça
                // como barra curta em vez de sumir: "quase nada" e "nada" são
                // fatos diferentes, e a diferença entre eles é a pergunta.
                height: Math.max(
                  Math.round((p.v / yMax) * alturaUtil),
                  p.v > 0 ? 2 : 0,
                ),
                background: AMBAR,
                opacity: alvo && hover !== i ? 0.55 : 1,
                borderRadius: "4px 4px 0 0",
              }}
            />
          </div>
        ))}
      </div>
      <div className="h-px w-full bg-slate-300 dark:bg-slate-700" />

      {/* Um número em cada barra seria ruído; o dia mais pesado é o que
          alguém procura numa série de consumo. */}
      {picoVisivel && pontos.length > 1 && (
        <div
          // `whitespace-nowrap` não é detalhe: sem ele "pico 2,58 kWh" quebra
          // em duas linhas dentro da faixa estreita e desce por cima das
          // barras. Rótulo direto que invade a marca deixa de ser rótulo.
          className="pointer-events-none absolute whitespace-nowrap text-[11px] font-semibold text-slate-600 dark:text-slate-300"
          style={{
            left: `${Math.min(88, Math.max(12, ((iPico + 0.5) / pontos.length) * 100))}%`,
            top: 0,
            transform: "translateX(-50%)",
          }}
        >
          pico {formatKwh(maxV)}
        </div>
      )}

      {alvo && (
        <div
          className="pointer-events-none absolute -top-1 z-10 rounded-md border border-surface-200 bg-white px-2 py-1 text-[11px] shadow-sm dark:border-slate-700 dark:bg-slate-900"
          style={{
            left: `${((hover! + 0.5) / pontos.length) * 100}%`,
            transform: `translateX(${hover! > pontos.length / 2 ? "-105%" : "5%"})`,
          }}
        >
          <div className="whitespace-nowrap font-semibold tabular-nums text-slate-800 dark:text-slate-100">
            {formatKwh(alvo.v)}
          </div>
          <div className="whitespace-nowrap text-slate-500 dark:text-slate-400">{formatDia(alvo.iso)}</div>
        </div>
      )}

      <div className="mt-1 flex justify-between text-[10px] text-slate-400 dark:text-slate-500">
        <span>{formatDia(pontos[0].iso)}</span>
        <span>{formatDia(pontos[pontos.length - 1].iso)}</span>
      </div>
    </div>
  );
}
